/**
 * Whether to mint and mount the Connect notification banner on the payout page.
 *
 * Kept out of the component so it can be tested: the banner mounts a
 * cross-origin Stripe iframe, which needs a real browser, so the decision lives
 * here and the page only maps state to JSX. Same split as `returnState.js`,
 * `refreshState.js` and `taxDocumentsState.js`.
 *
 * This decides whether to *mint* as well as whether to *mount*, and the mint has
 * a cost: a request without a session 401s, and the response interceptor clears
 * localStorage and hard-redirects home — the payee loses their session on top of
 * a banner they never asked for.
 *
 * Order matters, and each check earns its place:
 *
 * - WebView first. Stripe forbids embedded components outright in "embedded web
 *   views inside mobile or desktop applications". `/stripe/onboarding` can be
 *   opened inside the native CustomWebView with a `?token=` deep link, and the
 *   existing WebView swap only covers the `/stripe/return` path — so without
 *   this the default branch would mount an unsupported iframe. Nothing loads the
 *   page that way today (mobile has a native payout screen and opens hosted
 *   Stripe via the system browser), but the plumbing exists, so the guard is
 *   explicit rather than assumed.
 * - Signed-out before anything that fetches, for the 401 reason above. Same
 *   precedence the three sibling resolvers use.
 * - Unknown status before missing onboarding. `status` starts null and lands a
 *   round-trip later; reading absent as "not onboarded" would suppress the
 *   banner on first paint for exactly the payee who has an outstanding
 *   requirement, and only a later render would recover it. That conflation is
 *   the bug `returnState.js:51-54` documents having already been fixed once.
 */
export const BANNER_STATES = {
  /** Inside the mobile WebView, where Stripe does not support the component. */
  IN_WEB_VIEW: "inWebView",
  /** No session — a mint would 401 and take the user's session with it. */
  SIGNED_OUT: "signedOut",
  /** Identity present; waiting on the payout status. Never mint on a guess. */
  STATUS_UNKNOWN: "statusUnknown",
  /**
   * Stripe renders no banner UI without `details_submitted`, so there is nothing
   * to mint for. Covers both "no connected account" and "account exists but
   * onboarding not submitted" — the page shows its own setup CTA for both.
   */
  NOT_ONBOARDED: "notOnboarded",
  /** Cleared to mint; the session request is in flight. */
  REQUESTING: "requesting",
  /** Client secret in hand — the banner may mount. */
  READY: "ready",
  /**
   * The mint returned nothing, or the server reported it unavailable. Renders
   * nothing. Its own state rather than folded into NOT_ONBOARDED so the absence
   * is legible in tests and logs instead of looking like a user who never
   * onboarded.
   */
  HIDDEN: "hidden",
};

/**
 * One entry, and a title only.
 *
 * One entry because every non-READY state renders nothing at all, and Stripe's
 * banner is itself silent when there is no outstanding requirement — so there is
 * no empty state to write copy for. Inventing one would put "everything looks
 * fine" directly above a component about to say otherwise.
 *
 * Title only because the banner states its own specifics. A description here
 * could only paraphrase what the iframe is already about to say, and we cannot
 * see inside it to say anything more useful. Unlike the sibling resolvers, whose
 * `{title, description}` pairs feed a `Result` that owns the whole viewport.
 */
const COPY = {
  [BANNER_STATES.READY]: {
    title: "PAYOUT_ACTION_REQUIRED",
  },
};

/**
 * The state to render.
 *
 * `detailsSubmitted` comes from the page's live Connect status poll, not from
 * `receivingAccountVerify`, which lags the real status under Stripe. `unavailable`
 * is the server's own answer — it can disagree with the local flag when the
 * webhook has not landed yet, and that case has to hide rather than mount a
 * provider with no secret.
 */
export const resolveBannerState = ({
  signedIn,
  statusLoaded = false,
  detailsSubmitted,
  clientSecret,
  unavailable = false,
  failed = false,
  inWebView = false,
}) => {
  if (inWebView) {
    return BANNER_STATES.IN_WEB_VIEW;
  }
  if (!signedIn) {
    return BANNER_STATES.SIGNED_OUT;
  }
  if (!statusLoaded) {
    return BANNER_STATES.STATUS_UNKNOWN;
  }
  if (detailsSubmitted === false) {
    return BANNER_STATES.NOT_ONBOARDED;
  }
  if (unavailable || failed) {
    return BANNER_STATES.HIDDEN;
  }
  return clientSecret ? BANNER_STATES.READY : BANNER_STATES.REQUESTING;
};

/** i18n keys for a state, or null for the states that render nothing. */
export const getBannerCopy = (state) => COPY[state] ?? null;

/**
 * Only mint once there is an identity, a known status and an onboarded account.
 *
 * The resolver already encodes which state may fetch; a second inline check in
 * the component would be a copy of that rule able to drift from the one the
 * tests assert on.
 */
export const shouldRequestSession = (state) =>
  state === BANNER_STATES.REQUESTING;

/**
 * The only state holding a client secret.
 *
 * `ConnectComponentsProvider` with a null instance is not a safe no-op to lean
 * on, so the mount is gated on this rather than on the secret's truthiness at
 * the call site.
 */
export const canMountBanner = (state) => state === BANNER_STATES.READY;

/** Everything except READY renders nothing — no spinner, no empty state. */
export const isBannerHidden = (state) => state !== BANNER_STATES.READY;
